// src/App.js
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
  AppBar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArticleIcon from '@mui/icons-material/Article';
import LayersIcon from '@mui/icons-material/Layers';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

import DocumentProcessor from './components/DocumentProcessor/DocumentProcessor';
import ImageCountForm from './components/ImageCountForm';
import DocxUpload from './components/DocxUpload/DocxUpload';
import MetadataImport from './components/MetadataImport';
import ProductLookup from './components/ProductLookup';
import ProcessFlow from './components/ProcessFlow';
import DocxConversionFlow from './components/DocxConversionFlow';
import HomePage from './components/HomePage/HomePage';
import ProcessesOverview from './components/ProcessesOverview/ProcessesOverview';
import EcsControl from './components/EcsControl/EcsControl';
import PdfXmpEditor from './components/PdfXmpEditor/PdfXmpEditor';
import BookcardGenerator from './components/BookcardGenerator/BookcardGenerator';
import AddCoverService from './components/AddCoverService/AddCoverService';
import XmpCoverPipeline from './components/XmpCoverPipeline/XmpCoverPipeline';
import ProductionSetPipelineStatus from './components/ProductionSetPipelineStatus/ProductionSetPipelineStatus';
import { RequireAuth } from './auth/RequireAuth';
import { CallbackPage } from './auth/CallbackPage';
import { LogoutButton } from './auth/LogoutButton';

const drawerWidth = 240;

function AppContent() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Check if we're on a route that should be full-screen (no padding/centering)
  const isFullScreenRoute = location.pathname === '/chunks' || location.pathname.startsWith('/processes/');

  const drawer = (
    <div>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/" selected={location.pathname === '/'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/chunks" selected={location.pathname === '/chunks'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <ContentCutIcon />
              </ListItemIcon>
              <ListItemText primary="Chunk Editor" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/docx-upload" selected={location.pathname === '/docx-upload'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <CloudUploadIcon />
              </ListItemIcon>
              <ListItemText primary="DOCX Upload" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/count-images" selected={location.pathname === '/count-images'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <ImageSearchIcon />
              </ListItemIcon>
              <ListItemText primary="Image counter" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/edit-pdf" selected={location.pathname === '/edit-pdf'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <EditNoteIcon />
              </ListItemIcon>
              <ListItemText primary="XMP Editor" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/bookcard" selected={location.pathname === '/bookcard'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <ArticleIcon />
              </ListItemIcon>
              <ListItemText primary="Bookcard" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/add-cover" selected={location.pathname === '/add-cover'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <LayersIcon />
              </ListItemIcon>
              <ListItemText primary="Cover Binder" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/xmp-cover-pipeline" selected={location.pathname === '/xmp-cover-pipeline'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <PlaylistAddCheckIcon />
              </ListItemIcon>
              <ListItemText primary="XMP + Cover Pipeline" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={Link} to="/production-set-status" selected={location.pathname === '/production-set-status'} onClick={() => setMobileOpen(false)}>
              <ListItemIcon>
                <PictureAsPdfIcon />
              </ListItemIcon>
              <ListItemText primary="Production Set Status" />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1 }} />

          
        </List>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Production Tools
          </Typography>
          <EcsControl />
          <LogoutButton />
        </Toolbar>
      </AppBar>

      {/* Drawer for Desktop */}
      {!isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // Better mobile performance
          sx={{
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: isFullScreenRoute ? 0 : 3,  // No padding for full-screen routes
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/chunks" element={<DocumentProcessor />} />
          <Route path="/count-images" element={<Box mt={4} display="flex" justifyContent="center"><ImageCountForm /></Box>} />
          <Route path="/docx-upload" element={<DocxUpload />} />
          <Route path="/import-metadata" element={<Box mt={4} display="flex" justifyContent="center"><MetadataImport /></Box>} />
          <Route path="/product-lookup" element={<ProductLookup />} />
          <Route path="/processes" element={<ProcessesOverview />} />
          <Route path="/processes/ps-1-26" element={<ProcessFlow />} />
          <Route path="/processes/docx-conversion" element={<DocxConversionFlow />} />
          <Route path="/edit-pdf" element={<PdfXmpEditor />} />
          <Route path="/bookcard" element={<BookcardGenerator />} />
          <Route path="/add-cover" element={<AddCoverService />} />
          <Route path="/xmp-cover-pipeline" element={<XmpCoverPipeline />} />
          <Route path="/production-set-status" element={<ProductionSetPipelineStatus />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/callback" element={<CallbackPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AppContent />
            </RequireAuth>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;